import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Server, Wifi, AlertTriangle, Inbox, MessageSquare } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { Client as ClientType, Server as ServerType, Ticket as TicketType } from '../../types';

interface DashboardSummaryProps {}

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: number | string;
  color: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, color, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white p-6 rounded-2xl border border-gray-200 flex items-center space-x-4 shadow-sm hover:shadow-xl hover:scale-105 transition-all duration-300 ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${color}`}>
      {icon}
    </div>
    <div>
      <p className="text-md text-gray-500 font-medium">{title}</p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

export const DashboardSummary: React.FC<DashboardSummaryProps> = () => {
  const navigate = useNavigate();
  const data = useData();

  // Asignación segura de datos
  const clients = data.clients || [];
  const servers = data.servers || [];
  const tickets = data.tickets || [];

  const onlineServers = servers.filter(s => s.status === 'online').length;

  const expiringSoonServers = servers.filter(s => {
    if (!s.expirationDate) return false;
    const expirationDate = new Date(s.expirationDate);
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    return expirationDate <= sevenDaysFromNow;
  }).length;

  const openTickets = tickets.filter(t => t.status === 'abierto').length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <StatCard
        icon={<Users className="w-8 h-8 text-blue-600" />}
        title="Clientes"
        value={clients.length}
        color="bg-blue-100"
        onClick={() => navigate('/admin/clients')}
      />
      <StatCard
        icon={<Server className="w-8 h-8 text-purple-600" />}
        title="Servidores"
        value={servers.length}
        color="bg-purple-100"
        onClick={() => navigate('/admin/servers')}
      />
      <StatCard
        icon={<MessageSquare className="w-8 h-8 text-cyan-600" />}
        title="Tickets Totales"
        value={tickets.length}
        color="bg-cyan-100"
        onClick={() => navigate('/admin/tickets')}
      />
      <StatCard
        icon={<Wifi className="w-8 h-8 text-green-600" />}
        title="Online"
        value={onlineServers}
        color="bg-green-100"
        onClick={() => navigate('/admin/servers')}
      />
      <StatCard
        icon={<AlertTriangle className="w-8 h-8 text-orange-600" />}
        title="Expiran Pronto"
        value={expiringSoonServers}
        color="bg-orange-100"
        onClick={() => navigate('/admin/servers?filter=expiring')}
      />
      <StatCard
        icon={<Inbox className="w-8 h-8 text-red-600" />}
        title="Tickets Nuevos"
        value={openTickets}
        color="bg-red-100"
        onClick={() => navigate('/admin/tickets')}
      />
    </div>
  );
};
